
import React, { useState } from 'react';
import { 
  Plus, UserCheck, Mail, Phone, Shield, Calendar, Clock, 
  Trash2, Edit2, X, Save, Loader2, ChevronRight, Check,
  Palette, User, Info, AlertCircle, Ban
} from 'lucide-react';
import { Inspector, Availability } from '../types';
import { supabase, toSnakeCase, getErrorMessage, toCamelCase } from '../lib/supabase';

interface InspectorsProps {
  inspectors: Inspector[];
  setInspectors: React.Dispatch<React.SetStateAction<Inspector[]>>;
  availabilities: Availability[];
  setAvailabilities: React.Dispatch<React.SetStateAction<Availability[]>>;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const Inspectors: React.FC<InspectorsProps> = ({ inspectors, setInspectors, availabilities, setAvailabilities }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedInspectorId, setSelectedInspectorId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'availability'>('profile');

  const [formData, setFormData] = useState<Partial<Inspector>>({
    name: '', email: '', phone: '', licenseNumber: '', color: '#606c38', active: true
  });

  const selectedInspector = inspectors.find(i => i.id === selectedInspectorId);
  const inspectorAvail = availabilities.filter(a => a.inspectorId === selectedInspectorId);

  const handleSaveInspector = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    setSaving(true);
    try {
      const payload = toSnakeCase(formData);
      if (editingId) {
        const { error } = await supabase.from('inspectors').update(payload).eq('id', editingId);
        if (error) throw error;
        setInspectors(prev => prev.map(i => i.id === editingId ? { ...i, ...formData } as Inspector : i));
      } else {
        const { data, error } = await supabase.from('inspectors').insert([payload]).select().single();
        if (error) throw error;
        setInspectors(prev => [toCamelCase(data), ...prev]);
      }
      setIsSidebarOpen(false);
    } catch (err) { alert(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const toggleAvailability = async (dayIndex: number) => {
    if (!selectedInspectorId) return;
    const existing = inspectorAvail.find(a => a.dayOfWeek === dayIndex);
    
    try {
      if (existing) {
        await supabase.from('inspector_availability').delete().eq('id', existing.id);
        setAvailabilities(prev => prev.filter(a => a.id !== existing.id));
      } else {
        const payload = { 
          inspector_id: selectedInspectorId, 
          day_of_week: dayIndex, 
          start_time: '08:00', 
          end_time: '17:00' 
        };
        const { data, error } = await supabase.from('inspector_availability').insert([payload]).select().single();
        if (error) throw error;
        setAvailabilities(prev => [...prev, toCamelCase(data)]);
      }
    } catch (err) { alert(getErrorMessage(err)); }
  };

  const updateTimes = async (dayIndex: number, start: string, end: string) => {
    if (!selectedInspectorId) return;
    const existing = inspectorAvail.find(a => a.dayOfWeek === dayIndex);
    if (!existing) return;

    try {
      const { error } = await supabase.from('inspector_availability')
        .update({ start_time: start, end_time: end })
        .eq('id', existing.id);
      if (error) throw error;
      setAvailabilities(prev => prev.map(a => a.id === existing.id ? { ...a, startTime: start, endTime: end } : a));
    } catch (err) { alert(getErrorMessage(err)); }
  };

  return (
    <div className="animate-in fade-in duration-300">
      {selectedInspectorId && selectedInspector ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <button onClick={() => setSelectedInspectorId(null)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 transition-all"><ChevronRight size={20} className="rotate-180" /></button>
              <div>
                <h3 className="text-lg font-bold text-slate-800 leading-none">{selectedInspector.name}</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1.5">Assigned Badge: {selectedInspector.licenseNumber || 'PENDING'}</p>
              </div>
            </div>
            <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
               <button onClick={() => setActiveTab('profile')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'profile' ? 'bg-white text-[#606c38] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>Profile</button>
               <button onClick={() => setActiveTab('availability')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'availability' ? 'bg-white text-[#606c38] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>Availability</button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
             <div className="lg:col-span-1 space-y-6">
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
                   <div className="absolute top-0 right-0 w-16 h-16 opacity-10" style={{ backgroundColor: selectedInspector.color }}></div>
                   <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Credential Registry</h4>
                   <div className="space-y-4">
                      <div className="flex items-center gap-3 text-slate-600">
                         <Mail size={14} className="text-slate-300" />
                         <span className="text-xs font-bold truncate">{selectedInspector.email}</span>
                      </div>
                      <div className="flex items-center gap-3 text-slate-600">
                         <Phone size={14} className="text-slate-300" />
                         <span className="text-xs font-bold">{selectedInspector.phone || '--'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-slate-600">
                         <Shield size={14} className="text-slate-300" />
                         <span className="text-xs font-bold">L# {selectedInspector.licenseNumber || '--'}</span>
                      </div>
                   </div>
                   <div className="pt-6 mt-6 border-t border-slate-50 flex justify-between items-center">
                      <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${selectedInspector.active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                         {selectedInspector.active ? 'Registry Active' : 'Registry Locked'}
                      </span>
                      <button onClick={() => { setEditingId(selectedInspector.id); setFormData(selectedInspector); setIsSidebarOpen(true); }} className="text-slate-300 hover:text-blue-600 transition-colors"><Edit2 size={14} /></button>
                   </div>
                </div>

                <div className="bg-slate-900 rounded-[2rem] p-8 text-white shadow-xl relative overflow-hidden">
                   <div className="absolute top-0 right-0 p-8 text-white/5 -rotate-12 pointer-events-none"><User size={100} /></div>
                   <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-4">Internal ID</h4>
                   <p className="text-xs font-mono text-slate-400 truncate uppercase">{selectedInspector.id.substring(0, 18)}</p>
                </div>
             </div>

             <div className="lg:col-span-3">
                {activeTab === 'profile' ? (
                   <div className="bg-white p-8 md:p-12 rounded-[2rem] border border-slate-200 shadow-sm">
                      <div className="flex items-center gap-4 mb-8">
                         <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-black" style={{ backgroundColor: selectedInspector.color }}>
                            {selectedInspector.name.charAt(0)}
                         </div>
                         <div>
                            <h2 className="text-2xl font-black text-slate-900 tracking-tight">{selectedInspector.name}</h2>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Senior Technical Field Agent</p>
                         </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                         <div className="space-y-6">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-2">Active Protocols</h4>
                            <div className="space-y-3">
                               <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl flex items-center gap-4">
                                  <div className="p-2 bg-white rounded-lg text-[#606c38]"><Check size={14} /></div>
                                  <p className="text-xs font-bold text-slate-700 uppercase">Standard Home Audit</p>
                               </div>
                               <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl flex items-center gap-4">
                                  <div className="p-2 bg-white rounded-lg text-blue-500"><Check size={14} /></div>
                                  <p className="text-xs font-bold text-slate-700 uppercase">WDO / Termite Sync</p>
                               </div>
                            </div>
                         </div>
                         <div className="bg-blue-50/30 border border-blue-100 p-8 rounded-[2rem] flex flex-col justify-between">
                            <div>
                               <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-4">Tech Support Note</h4>
                               <p className="text-xs text-slate-500 font-medium leading-relaxed">Assigning this inspector to a session will automatically apply their "Brand Color" to the global operations hub calendar.</p>
                            </div>
                            <div className="flex items-center gap-2 mt-6">
                               <div className="w-8 h-8 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: selectedInspector.color }}></div>
                               <span className="text-[10px] font-black text-slate-800 uppercase">Operational Tint</span>
                            </div>
                         </div>
                      </div>
                   </div>
                ) : (
                   <div className="bg-white p-8 md:p-10 rounded-[2rem] border border-slate-200 shadow-sm">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                         <div>
                            <h2 className="text-xl font-black text-slate-900 tracking-tight uppercase">Registry Hours</h2>
                            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Define recurring availability slots</p>
                         </div>
                         <div className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center gap-2 border border-blue-100">
                            <Clock size={12} /> Auto-Validation Active
                         </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                         {DAYS.map((day, idx) => {
                            const avail = inspectorAvail.find(a => a.dayOfWeek === idx);
                            return (
                               <div key={day} className={`p-5 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${avail ? 'bg-white border-[#606c38] shadow-md' : 'bg-slate-50 border-slate-200 opacity-60'}`}>
                                  <div className="flex items-center gap-4">
                                     <button onClick={() => toggleAvailability(idx)} className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${avail ? 'bg-[#606c38] text-white shadow-lg shadow-[#606c38]/20' : 'bg-white text-slate-300 border border-slate-200 hover:text-slate-600'}`}>
                                        {avail ? <Check size={20} /> : <Ban size={20} />}
                                     </button>
                                     <div>
                                        <h5 className={`font-black text-sm uppercase tracking-tight ${avail ? 'text-slate-900' : 'text-slate-400'}`}>{day}</h5>
                                        <p className="text-[9px] font-bold uppercase text-slate-400">{avail ? 'Open for Scheduling' : 'Registry Locked'}</p>
                                     </div>
                                  </div>
                                  {avail && (
                                     <div className="flex items-center gap-3 animate-in fade-in slide-in-from-right-2">
                                        <div className="space-y-1">
                                           <label className="text-[8px] font-black text-slate-400 uppercase block">Start</label>
                                           <input type="time" className="p-2 bg-slate-50 border border-slate-100 rounded-lg text-xs font-bold outline-none focus:ring-1 focus:ring-[#606c38]" value={avail.startTime} onChange={(e) => updateTimes(idx, e.target.value, avail.endTime)} />
                                        </div>
                                        <div className="w-4 h-px bg-slate-200 mt-4"></div>
                                        <div className="space-y-1">
                                           <label className="text-[8px] font-black text-slate-400 uppercase block">End</label>
                                           <input type="time" className="p-2 bg-slate-50 border border-slate-100 rounded-lg text-xs font-bold outline-none focus:ring-1 focus:ring-[#606c38]" value={avail.endTime} onChange={(e) => updateTimes(idx, avail.startTime, e.target.value)} />
                                        </div>
                                     </div>
                                  )}
                               </div>
                            );
                         })}
                      </div>
                   </div>
                )}
             </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
               <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase">Inspector Registry</h3>
               <p className="text-sm text-slate-500 font-medium">Manage technical field personnel and availability protocols.</p>
            </div>
            <button onClick={() => { setEditingId(null); setFormData({name: '', email: '', phone: '', licenseNumber: '', color: '#606c38', active: true}); setIsSidebarOpen(true); }} className="bg-slate-900 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-slate-800 transition-all flex items-center justify-center gap-2"><Plus size={18} /> Add Inspector</button>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto no-scrollbar">
            <table className="w-full text-left min-w-max">
              <thead><tr className="bg-slate-50/50 border-b border-slate-200"><th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Technician Profile</th><th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Badge / License</th><th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest">Assigned Color</th><th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Status</th><th className="px-6 py-5 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Actions</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {inspectors.map(inspector => (
                  <tr key={inspector.id} onClick={() => setSelectedInspectorId(inspector.id)} className="hover:bg-slate-50/80 transition-colors cursor-pointer group">
                    <td className="px-6 py-5">
                       <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-sm shadow-md" style={{ backgroundColor: inspector.color }}>{inspector.name.charAt(0)}</div>
                          <div className="min-w-0">
                             <p className="text-sm font-bold text-slate-800 truncate">{inspector.name}</p>
                             <p className="text-[10px] text-slate-400 font-bold truncate uppercase">{inspector.email}</p>
                          </div>
                       </div>
                    </td>
                    <td className="px-6 py-5"><span className="text-[10px] font-mono font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded">L# {inspector.licenseNumber || 'PENDING'}</span></td>
                    <td className="px-6 py-5"><div className="flex items-center gap-2"><div className="w-4 h-4 rounded-full border border-slate-200" style={{ backgroundColor: inspector.color }}></div><span className="text-[10px] font-bold text-slate-400 uppercase font-mono">{inspector.color}</span></div></td>
                    <td className="px-6 py-5 text-center"><span className={`text-[8px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider ${inspector.active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-300'}`}>{inspector.active ? 'Active' : 'Inactive'}</span></td>
                    <td className="px-6 py-5 text-right"><div className="flex items-center justify-end gap-2"><button onClick={(e) => { e.stopPropagation(); setEditingId(inspector.id); setFormData(inspector); setIsSidebarOpen(true); }} className="p-2 text-slate-300 hover:text-blue-600 transition-all"><Edit2 size={18} /></button><ChevronRight size={18} className="text-slate-200" /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Editor Sidebar */}
      <div className={`fixed inset-0 z-[300] ${isSidebarOpen ? 'visible' : 'invisible'}`}>
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300" onClick={() => setIsSidebarOpen(false)} />
        <div className={`absolute inset-y-0 right-0 w-full md:max-w-xl bg-white shadow-2xl transition-transform duration-500 ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
          <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
            <h3 className="font-bold text-slate-800 text-xs uppercase tracking-widest">{editingId ? 'Modify Inspector Registry' : 'New Personnel Record'}</h3>
            <button onClick={() => setIsSidebarOpen(false)} className="p-2 hover:bg-white rounded-xl text-slate-400 transition-all"><X size={24} /></button>
          </div>
          <form onSubmit={handleSaveInspector} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 no-scrollbar bg-white">
             <div className="space-y-6">
                <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Legal Identity</label><input required className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm focus:ring-1 focus:ring-[#606c38] outline-none" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Full name..." /></div>
                <div className="grid grid-cols-2 gap-4">
                   <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Registry Email</label><input required type="email" className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm focus:ring-1 focus:ring-[#606c38] outline-none" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="email@address.com" /></div>
                   <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Operational Mobile</label><input className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm focus:ring-1 focus:ring-[#606c38] outline-none" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(000) 000-0000" /></div>
                </div>
                <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Credential Badge / License Number</label><input className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm focus:ring-1 focus:ring-[#606c38] outline-none" value={formData.licenseNumber} onChange={(e) => setFormData({...formData, licenseNumber: e.target.value})} placeholder="L# 123456789" /></div>
                
                <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-4">
                   <div className="flex justify-between items-center">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Brand Operational Tint</label>
                      <input type="color" className="w-10 h-10 rounded-full cursor-pointer bg-transparent border-none" value={formData.color} onChange={(e) => setFormData({...formData, color: e.target.value})} />
                   </div>
                   <div className="flex items-center gap-3">
                      <input type="checkbox" className="w-5 h-5 rounded-lg border-slate-300 text-[#606c38] focus:ring-[#606c38]" checked={formData.active} onChange={(e) => setFormData({...formData, active: e.target.checked})} />
                      <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Registry Active & Operational</span>
                   </div>
                </div>
             </div>
          </form>
          <div className="p-6 md:p-8 border-t border-slate-100 flex flex-col sm:flex-row gap-4 bg-white shadow-inner">
             <button type="button" onClick={() => setIsSidebarOpen(false)} className="w-full sm:flex-1 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 rounded-2xl border border-slate-200 transition-all">Abort</button>
             <button disabled={saving} type="submit" onClick={handleSaveInspector} className="w-full sm:flex-[2] py-4 bg-slate-900 text-white font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-xl">{saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Commit Record</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Inspectors;
